import type { ModelSummaryResponse } from "../../types/api";
import { Card } from "../ui/Card";

interface ModelSummaryPanelProps {
  summary: ModelSummaryResponse | null;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
}

export function ModelSummaryPanel({ error, status, summary }: ModelSummaryPanelProps) {
  return (
    <Card className="space-y-5">
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">Model summary</p>
        <h3 className="font-display text-2xl font-semibold text-white">NH-LoRA artifact runtime</h3>
      </div>

      {error ? <div className="rounded-[24px] border border-[#56222e] bg-[#2a1218] p-4 text-sm text-[#ffd4dc]">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <StatCard label="Artifact version" value={summary?.artifact_version ?? loadingValue(status)} />
        <StatCard label="Backbone" value={summary?.backbone_name ?? loadingValue(status)} />
        <StatCard label="Classes" value={summary?.num_classes ? String(summary.num_classes) : loadingValue(status)} />
        <StatCard label="Image size" value={summary?.image_size ? `${summary.image_size}px` : loadingValue(status)} />
        <StatCard label="Runtime" value={summary ? `${summary.runtime.device} · ${summary.runtime.warmup_state}` : loadingValue(status)} />
        <StatCard label="Model size" value={summary?.stats.model_size_mb ? `${summary.stats.model_size_mb.toFixed(1)} MB` : "Unavailable"} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <DetailCard
          label="Source repo"
          value={summary?.repo_id ?? "MODEL_REPO_ID not configured"}
          secondary={summary?.checkpoint_file ? `Checkpoint · ${summary.checkpoint_file}` : null}
        />
        <DetailCard
          label="Preprocess"
          value={
            summary?.preprocessing
              ? `${summary.preprocessing.color_mode} · resize ${summary.preprocessing.resize_size} · crop ${summary.preprocessing.crop_size}`
              : loadingValue(status)
          }
          secondary={
            summary?.inference_profile_available !== undefined && summary?.inference_profile_available !== null
              ? `Inference profile ${summary.inference_profile_available ? "available" : "fallback"}`
              : null
          }
        />
      </div>
    </Card>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[24px] border border-white/8 bg-white/[0.03] p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">{label}</p>
      <p className="mt-3 text-base font-semibold text-white">{value}</p>
    </div>
  );
}

function DetailCard({ label, value, secondary }: { label: string; value: string; secondary: string | null }) {
  return (
    <div className="rounded-[24px] border border-white/8 bg-white/[0.03] p-5">
      <p className="mb-2 text-xs uppercase tracking-[0.18em] text-ink-muted">{label}</p>
      <p className="text-sm font-medium text-white">{value}</p>
      {secondary ? <p className="mt-2 text-xs text-ink-soft">{secondary}</p> : null}
    </div>
  );
}

function loadingValue(status: string): string {
  return status === "loading" ? "Loading..." : "Unavailable";
}
